import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { StatusBadge as AppStatusBadge } from '@/components/ui/status-badge';
import { Download, Mail, UserX, Trash2, Loader2, AlertTriangle, Camera } from 'lucide-react';
import { Membre, getAttestationStatus } from '@/hooks/queries/useMembresQuery';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface MembreSheetProps {
  membre: Membre | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'd MMMM yyyy', { locale: fr });
  } catch {
    return '—';
  }
}

function StatusBadge({ status }: { status: ReturnType<typeof getAttestationStatus> }) {
  switch (status) {
    case 'conforme':
      return <AppStatusBadge tone="success">Conforme</AppStatusBadge>;
    case 'en_attente':
      return <AppStatusBadge tone="warning">En attente</AppStatusBadge>;
    case 'verification_en_cours':
      return (
        <AppStatusBadge tone="info">
          <Loader2 className="w-3 h-3 animate-spin" />
          Vérification en cours
        </AppStatusBadge>
      );
    case 'expiree':
      return <AppStatusBadge tone="danger">Expirée</AppStatusBadge>;
    case 'a_regulariser':
      return <AppStatusBadge tone="danger">À régulariser</AppStatusBadge>;
  }
}

/**
 * Panel latéral affichant le détail d'un membre
 * Slide depuis la droite au clic sur une ligne du tableau
 */
export const MembreSheet = ({ membre, open, onOpenChange }: MembreSheetProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isMarking, setIsMarking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendingMiseEnDemeure, setIsSendingMiseEnDemeure] = useState(false);
  const [isLoadingScreenshot, setIsLoadingScreenshot] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMiseEnDemeureConfirm, setShowMiseEnDemeureConfirm] = useState(false);

  if (!membre) return null;

  const status = getAttestationStatus(membre);
  const initials = `${membre.prenom[0]}${membre.nom[0]}`.toUpperCase();
  const contratLabel = membre.type_contrat === 'Benevole' ? 'Bénévole' : membre.type_contrat === 'Interim' ? 'Intérim' : membre.type_contrat;

  /**
   * Marquer un membre comme parti (soft delete via statut_archivage)
   */
  const handleMarquerCommeParti = async () => {
    setIsMarking(true);
    try {
      const { error } = await supabase
        .from('membres')
        .update({ 
          statut_archivage: 'parti',
          date_depart: new Date().toISOString().split('T')[0],
        })
        .eq('id', membre.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['membres'] });
      toast({
        title: 'Membre archivé',
        description: `${membre.prenom} ${membre.nom} a été marqué comme parti.`,
      });
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.message || 'Impossible de mettre à jour le membre',
        variant: 'destructive',
      });
    } finally {
      setIsMarking(false);
    }
  };

  /**
   * Supprimer définitivement un membre et ses attestations
   */
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // Supprimer les attestations liées d'abord (FK)
      const { error: attError } = await supabase
        .from('attestations')
        .delete()
        .eq('membre_id', membre.id);

      if (attError) throw attError;

      // Supprimer les logs email liés
      await supabase
        .from('emails_log')
        .delete()
        .eq('membre_id', membre.id);

      // Supprimer le membre
      const { error } = await supabase
        .from('membres')
        .delete()
        .eq('id', membre.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['membres'] });
      toast({
        title: 'Membre supprimé',
        description: `${membre.prenom} ${membre.nom} a été supprimé définitivement.`,
      });
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.message || 'Impossible de supprimer le membre',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  /**
   * Envoyer un rappel par email (demande d'attestation)
   */
  const handleEnvoyerRappel = async () => {
    try {
      // Appeler l'Edge Function d'envoi email
      const { error } = await supabase.functions.invoke('send-attestation-request', {
        body: {
          membre_id: membre.id,
          email: membre.email,
          prenom: membre.prenom,
          nom: membre.nom,
          etablissement_id: membre.etablissement_id,
          type: 'rappel',
        },
      });

      if (error) throw error;

      toast({
        title: 'Rappel envoyé',
        description: `Un email de rappel a été envoyé à ${membre.prenom} ${membre.nom}.`,
      });
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.message || "Impossible d'envoyer le rappel",
        variant: 'destructive',
      });
    }
  };

  /**
   * Envoyer une mise en demeure (Email 7) — action manuelle admin.
   * Nécessite une attestation existante pour ce membre (créée dès la
   * demande initiale ou une relance précédente).
   */
  const handleEnvoyerMiseEnDemeure = async () => {
    if (!membre.attestation?.id) {
      toast({
        title: 'Impossible d\'envoyer la mise en demeure',
        description: 'Aucune attestation associée à ce membre.',
        variant: 'destructive',
      });
      setShowMiseEnDemeureConfirm(false);
      return;
    }

    setIsSendingMiseEnDemeure(true);
    try {
      const { error } = await supabase.functions.invoke('send-mise-en-demeure', {
        body: {
          attestation_id: membre.attestation.id,
          membre_id: membre.id,
          etablissement_id: membre.etablissement_id,
        },
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['membres'] });
      toast({
        title: 'Mise en demeure envoyée',
        description: `Une mise en demeure a été envoyée à ${membre.prenom} ${membre.nom}.`,
      });
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.message || "Impossible d'envoyer la mise en demeure",
        variant: 'destructive',
      });
    } finally {
      setIsSendingMiseEnDemeure(false);
      setShowMiseEnDemeureConfirm(false);
    }
  };

  /**
   * Ouvre dans un nouvel onglet la capture d'écran de la dernière vérification
   * gouvernementale (attestation_verifications.screenshot_storage_path).
   */
  const handleVoirCaptureVerification = async () => {
    if (!membre.attestation?.id) return;

    setIsLoadingScreenshot(true);
    try {
      const { data: verification, error: verificationError } = await supabase
        .from('attestation_verifications')
        .select('screenshot_storage_path')
        .eq('attestation_id', membre.attestation.id)
        .not('screenshot_storage_path', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (verificationError) throw verificationError;

      if (!verification?.screenshot_storage_path) {
        toast({
          title: 'Aucune capture disponible',
          description: "Aucune capture d'écran de vérification n'a été enregistrée pour cette attestation.",
          variant: 'destructive',
        });
        return;
      }

      const { data, error } = await supabase.storage
        .from('attestation-screenshots')
        .createSignedUrl(verification.screenshot_storage_path, 60);

      if (error) throw error;

      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.message || "Impossible de récupérer la capture d'écran",
        variant: 'destructive',
      });
    } finally {
      setIsLoadingScreenshot(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[440px] overflow-y-auto border-l border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface))] p-0 shadow-[0_20px_60px_rgba(15,23,42,0.16)] sm:max-w-[440px]">
          <SheetHeader className="border-b border-[hsl(var(--app-border-subtle))] px-6 pb-5 pt-6">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarFallback
                  className="text-white font-medium text-sm"
                  style={{ backgroundColor: '#40E0D0' }}
                >
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div>
                <SheetTitle className="text-[18px] font-semibold tracking-[-0.02em] text-[hsl(var(--app-text-strong))]">
                  {membre.prenom} {membre.nom}
                </SheetTitle>
                <SheetDescription className="mt-1 text-[14px] leading-6 text-[hsl(var(--app-text-muted))]">
                  {membre.poste || contratLabel} · {contratLabel} depuis {membre.date_embauche ? format(new Date(membre.date_embauche), 'MMM yyyy', { locale: fr }) : '—'}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-0">
            {/* Section Informations */}
            <section className="border-b border-[hsl(var(--app-border-subtle))] px-6 py-5">
              <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
                Informations
              </h3>
              <div className="space-y-2">
                <InfoRow label="Email" value={membre.email} />
                <InfoRow label="Poste" value={membre.poste || '—'} />
                <InfoRow label="Contrat" value={contratLabel} />
                <InfoRow label="Date d'embauche" value={formatDate(membre.date_embauche)} />
              </div>
            </section>

            {/* Section Attestation d'honorabilité */}
            <section className="border-b border-[hsl(var(--app-border-subtle))] px-6 py-5">
              <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
                Attestation d'honorabilité
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-[13px] text-[hsl(var(--app-text-muted))]">Statut</span>
                  <StatusBadge status={status} />
                </div>
                {status === 'verification_en_cours' && (
                  <div className="rounded-[14px] border border-sky-200 bg-sky-50 p-3">
                    <p className="text-[12px] leading-5 text-sky-700">
                      Le document a été reçu et est en cours de vérification. Cette étape peut prendre quelques minutes.
                    </p>
                  </div>
                )}
                <InfoRow 
                  label="Date de délivrance" 
                  value={formatDate(membre.attestation?.date_delivrance ?? null)} 
                />
                <InfoRow 
                  label="Validité" 
                  value={membre.attestation?.date_expiration 
                    ? `Valide jusqu'au ${formatDate(membre.attestation.date_expiration)}` 
                    : '—'
                  } 
                />
                <InfoRow 
                  label="Renouvellement" 
                  value={formatDate(membre.attestation?.date_renouvellement ?? null)} 
                />
                
                {/* Document PDF si disponible */}
                {membre.attestation?.nom_fichier && (
                  <div className="mt-3 flex items-center gap-3 rounded-[16px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] p-3">
                    <div className="rounded-[12px] border border-[hsl(var(--app-border-subtle))] bg-white p-2">
                      <svg className="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 18H17V16H7V18M17 14H7V12H17V14M7 10H11V8H7V10M15 4H5V20H19V8L15 4M15 9V5L19 9H15Z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-[13px] font-medium text-[hsl(var(--app-text-strong))]">
                        {membre.attestation.nom_fichier}
                      </p>
                      <p className="text-[12px] text-[hsl(var(--app-text-muted))]">
                        {membre.attestation.verified_at 
                          ? `Vérifiée le ${formatDate(membre.attestation.verified_at)}`
                          : 'En attente de vérification'
                        }
                      </p>
                    </div>
                    <button className="rounded-lg p-1.5 transition-colors hover:bg-white">
                      <Download className="h-4 w-4 text-[hsl(var(--app-text-muted))]" />
                    </button>
                  </div>
                )}
              </div>
            </section>

            {/* Section Prise de référence */}
            <section className="border-b border-[hsl(var(--app-border-subtle))] px-6 py-5">
              <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
                Prise de référence
              </h3>
              <div className="py-2 text-[13px] text-[hsl(var(--app-text-muted))]">
                Aucune prise de référence associée.
              </div>
            </section>

            {/* Section Actions */}
            <section className="space-y-2 px-6 py-5">
              <Button 
                variant="outline" 
                className="h-11 w-full justify-start gap-2 rounded-[14px] border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text))]"
                disabled
                title="Fonctionnalité à venir"
              >
                <Download className="w-4 h-4" />
                Télécharger le dossier complet
              </Button>
              <Button 
                variant="outline" 
                className="h-11 w-full justify-start gap-2 rounded-[14px] border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text))]"
                onClick={handleEnvoyerRappel}
              >
                <Mail className="w-4 h-4" />
                Envoyer un rappel
              </Button>
              {membre.attestation?.id && (
                <Button
                  variant="outline"
                  className="h-11 w-full justify-start gap-2 rounded-[14px] border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text))]"
                  onClick={handleVoirCaptureVerification}
                  disabled={isLoadingScreenshot}
                >
                  <Camera className="w-4 h-4" />
                  {isLoadingScreenshot ? 'Chargement...' : 'Voir la capture de vérification'}
                </Button>
              )}
              {(status === 'a_regulariser' || status === 'expiree') && (
                <Button
                  variant="outline"
                  className="h-11 w-full justify-start gap-2 rounded-[14px] border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  onClick={() => setShowMiseEnDemeureConfirm(true)}
                  disabled={isSendingMiseEnDemeure || !!membre.attestation?.mise_en_demeure_envoyee_at}
                >
                  <AlertTriangle className="w-4 h-4" />
                  {membre.attestation?.mise_en_demeure_envoyee_at
                    ? 'Mise en demeure déjà envoyée'
                    : 'Envoyer une mise en demeure'}
                </Button>
              )}
              <Button 
                variant="outline" 
                className="h-11 w-full justify-start gap-2 rounded-[14px] border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={handleMarquerCommeParti}
                disabled={isMarking}
              >
                <UserX className="w-4 h-4" />
                {isMarking ? 'Archivage...' : 'Marquer comme parti'}
              </Button>
              <Button 
                variant="outline" 
                className="h-11 w-full justify-start gap-2 rounded-[14px] border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
              >
                <Trash2 className="w-4 h-4" />
                Supprimer définitivement
              </Button>
            </section>
          </div>
        </SheetContent>
      </Sheet>

      {/* Dialog de confirmation de suppression */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="app-content-centered-dialog rounded-[20px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface))] shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce membre ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le membre <strong>{membre.prenom} {membre.nom}</strong>, 
              ses attestations et ses logs seront supprimés définitivement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmation d'envoi de mise en demeure */}
      <AlertDialog open={showMiseEnDemeureConfirm} onOpenChange={setShowMiseEnDemeureConfirm}>
        <AlertDialogContent className="app-content-centered-dialog rounded-[20px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface))] shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Envoyer une mise en demeure ?</AlertDialogTitle>
            <AlertDialogDescription>
              Un email de mise en demeure sera envoyé à <strong>{membre.prenom} {membre.nom}</strong>,
              citant l'article L.133-6 du CASF et le décret n°2026-324. Cette action déclenche une
              notification in-app persistante et non désactivable, nécessitant un suivi de votre part.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSendingMiseEnDemeure}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEnvoyerMiseEnDemeure}
              disabled={isSendingMiseEnDemeure}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isSendingMiseEnDemeure ? 'Envoi...' : 'Envoyer la mise en demeure'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-1.5">
      <span className="text-[13px] text-[hsl(var(--app-text-muted))]">{label}</span>
      <span className="text-[13px] font-medium text-[hsl(var(--app-text))]">{value}</span>
    </div>
  );
}
